import React, { useState } from 'react';
import { 
  PencilIcon, 
  CheckIcon, 
  XMarkIcon,
  // InformationCircleIcon, // Unused - removed
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const VesselDetailsTab = ({ vessel, onVesselUpdate, user }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    // Basic Info
    vessel_name: vessel?.vessel_name || '',
    vessel_type: vessel?.vessel_type || 'sailboat',
    year_built: vessel?.year_built || '',
    manufacturer: vessel?.manufacturer || '',
    model: vessel?.model || '',
    hull_identification_number: vessel?.hull_identification_number || '',
    brand: vessel?.brand || '',
    flag: vessel?.flag || 'Canada',
    port_of_registry: vessel?.port_of_registry || '',
    
    // Credentials
    uscg_official_number: vessel?.uscg_official_number || '',
    registration_number: vessel?.registration_number || '',
    imo: vessel?.imo || '',
    mmsi: vessel?.mmsi || '',
    callsign: vessel?.callsign || '',
    epirb: vessel?.epirb || '',
    
    // Dimensions
    length_overall: vessel?.length_overall || '',
    beam: vessel?.beam || '',
    draft: vessel?.draft || '',
    air_draft: vessel?.air_draft || '',
    displacement: vessel?.displacement || '',
    
    // Hull Details
    hull_material: vessel?.hull_material || '',
    hull_type: vessel?.hull_type || 'Monohull',
    hull_color: vessel?.hull_color || '',
    
    // Engine Details
    engine_make: vessel?.engine_make || '',
    engine_model: vessel?.engine_model || '',
    engine_horsepower: vessel?.engine_horsepower || '',
    engine_year: vessel?.engine_year || '',
    fuel_type: vessel?.fuel_type || '',
    fuel_capacity: vessel?.fuel_capacity || '',
    engine_hours: vessel?.engine_hours || '',
    
    // Tank Capacities
    fresh_water_capacity: vessel?.fresh_water_capacity || '',
    waste_water_capacity: vessel?.waste_water_capacity || '',
    holding_tank_capacity: vessel?.holding_tank_capacity || '',
    
    // Electrical
    voltage: vessel?.voltage || '12v',
    shorepower: vessel?.shorepower || '125 volt | 30 amp',
    shorepower_qty: vessel?.shorepower_qty || 'Dual',
    battery_capacity: vessel?.battery_capacity || '',
    alternator_output: vessel?.alternator_output || '',
    
    // Performance
    max_speed: vessel?.max_speed || '',
    cruising_speed: vessel?.cruising_speed || '',
    range: vessel?.range || '',
    
    // Accommodations
    berths: vessel?.berths || '',
    cabins: vessel?.cabins || '',
    heads: vessel?.heads || '',
    galley: vessel?.galley || '',
    
    // Safety Equipment
    life_rafts: vessel?.life_rafts || '',
    life_jackets: vessel?.life_jackets || '',
    fire_extinguishers: vessel?.fire_extinguishers || '',
    flares: vessel?.flares || '',
    
    // Insurance & Documentation
    insurance_company: vessel?.insurance_company || '',
    insurance_policy: vessel?.insurance_policy || '',
    insurance_expiry: vessel?.insurance_expiry || '',
    survey_date: vessel?.survey_date || '',
    survey_expiry: vessel?.survey_expiry || '',
    
    // Additional Info
    purchase_date: vessel?.purchase_date || '',
    purchase_price: vessel?.purchase_price || '',
    current_value: vessel?.current_value || '',
    notes: vessel?.notes || ''
  });

  const vesselTypes = [
    { value: 'sailboat', label: 'Sailboat' },
    { value: 'powerboat', label: 'Powerboat' },
    { value: 'yacht', label: 'Yacht' },
    { value: 'catamaran', label: 'Catamaran' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'other', label: 'Other' }
  ];

  const hullTypes = [
    { value: 'Monohull', label: 'Monohull' },
    { value: 'Catamaran', label: 'Catamaran' },
    { value: 'Trimaran', label: 'Trimaran' },
    { value: 'Multihull', label: 'Multihull' }
  ];

  const hullMaterials = [
    { value: 'Fiberglass', label: 'Fiberglass' },
    { value: 'Steel', label: 'Steel' },
    { value: 'Aluminum', label: 'Aluminum' },
    { value: 'Wood', label: 'Wood' },
    { value: 'Composite', label: 'Composite' },
    { value: 'Other', label: 'Other' }
  ];

  const fuelTypes = [
    { value: 'Diesel', label: 'Diesel' },
    { value: 'Gasoline', label: 'Gasoline' },
    { value: 'Electric', label: 'Electric' },
    { value: 'Hybrid', label: 'Hybrid' },
    { value: 'Other', label: 'Other' }
  ];

  const voltages = [
    { value: '12v', label: '12v' },
    { value: '24v', label: '24v' },
    { value: '110v', label: '110v' },
    { value: '220v', label: '220v' },
    { value: '12v/24v', label: '12v/24v' },
    { value: '12v/110v', label: '12v/110v' }
  ];

  const shorepowerOptions = [
    { value: '125 volt | 30 amp', label: '125 volt | 30 amp' },
    { value: '125 volt | 50 amp', label: '125 volt | 50 amp' },
    { value: '250 volt | 30 amp', label: '250 volt | 30 amp' },
    { value: '250 volt | 50 amp', label: '250 volt | 50 amp' },
    { value: 'Dual 125 volt | 30 amp', label: 'Dual 125 volt | 30 amp' },
    { value: 'Dual 250 volt | 50 amp', label: 'Dual 250 volt | 50 amp' }
  ];

  const shorepowerQtyOptions = [
    { value: 'Single', label: 'Single' },
    { value: 'Dual', label: 'Dual' },
    { value: 'Triple', label: 'Triple' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/vessels/${vessel.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update vessel');
      }

      toast.success('Vessel details updated successfully');
      setIsEditing(false);
      onVesselUpdate();
    } catch (error) {
      console.error('Error updating vessel:', error);
      toast.error(error.message || 'Failed to update vessel');
    }
  };

  const handleCancel = () => {
    setFormData({
      vessel_name: vessel?.vessel_name || '',
      vessel_type: vessel?.vessel_type || 'sailboat',
      year_built: vessel?.year_built || '',
      manufacturer: vessel?.manufacturer || '',
      model: vessel?.model || '',
      // ... reset all fields
    });
    setIsEditing(false);
  };

  const renderField = (label, name, type = 'text', options = null, required = false) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {type === 'select' ? (
        <select
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          disabled={!isEditing}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500 ${
            !isEditing ? 'bg-gray-50' : 'bg-white'
          }`}
        >
          <option value="">Select {label}</option>
          {options?.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          disabled={!isEditing}
          className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-marine-500 focus:border-marine-500 ${
            !isEditing ? 'bg-gray-50' : 'bg-white'
          }`}
        />
      )}
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Vessel Details</h2>
        <div className="flex space-x-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="btn btn-primary flex items-center"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              Edit Details
            </button>
          ) : (
            <>
              <button
                onClick={handleSave}
                className="btn btn-success flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" />
                Save Changes
              </button>
              <button
                onClick={handleCancel}
                className="btn btn-secondary flex items-center"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* HIN Warning */}
      {vessel?.hull_identification_number && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm text-yellow-800">
                <strong>The Hull Identification Number (HIN) provided is not valid.</strong> The manufacturer and year of this vessel will not be auto populated.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* General Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">General</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Year', 'year_built', 'number', null, true)}
          {renderField('Manufacturer', 'manufacturer', 'text', null, true)}
          {renderField('Status', 'status', 'select', [
            { value: 'In Service', label: 'In Service' },
            { value: 'Out of Service', label: 'Out of Service' },
            { value: 'Under Repair', label: 'Under Repair' },
            { value: 'For Sale', label: 'For Sale' }
          ])}
          {renderField('Type', 'vessel_type', 'select', vesselTypes, true)}
          {renderField('Hull Identification Number (HIN)', 'hull_identification_number', 'text')}
          {renderField('Brand', 'brand', 'text')}
          {renderField('Model', 'model', 'text', null, true)}
          {renderField('Flag', 'flag', 'text')}
          {renderField('Port of Registry', 'port_of_registry', 'text')}
        </div>
      </div>

      {/* Credentials Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Credentials</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('USCG Official #', 'uscg_official_number', 'text')}
          {renderField('Registration #', 'registration_number', 'text')}
          {renderField('IMO', 'imo', 'text')}
          {renderField('MMSI', 'mmsi', 'text')}
          {renderField('Callsign', 'callsign', 'text')}
          {renderField('EPIRB', 'epirb', 'text')}
        </div>
      </div>

      {/* Dimensions Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Dimensions</h3>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Imperial (US)</span>
            <button className="relative inline-flex h-6 w-11 items-center rounded-full bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              <span className="inline-block h-4 w-4 transform rounded-full bg-white transition-transform translate-x-1" />
            </button>
            <span className="text-sm text-gray-500">Metric</span>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Beam', 'beam', 'number')}
          {renderField('Length', 'length_overall', 'number')}
          {renderField('Draft', 'draft', 'number')}
          {renderField('Air Draft', 'air_draft', 'number')}
          {renderField('Displacement', 'displacement', 'number')}
        </div>
      </div>

      {/* Hull Details Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Hull Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Hull Type', 'hull_type', 'select', hullTypes)}
          {renderField('Hull Material', 'hull_material', 'select', hullMaterials)}
          {renderField('Hull Color', 'hull_color', 'text')}
        </div>
      </div>

      {/* Engine Details Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Engine Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Engine Make', 'engine_make', 'text')}
          {renderField('Engine Model', 'engine_model', 'text')}
          {renderField('Engine Year', 'engine_year', 'number')}
          {renderField('Horsepower', 'engine_horsepower', 'number')}
          {renderField('Fuel Type', 'fuel_type', 'select', fuelTypes)}
          {renderField('Fuel Capacity', 'fuel_capacity', 'number')}
          {renderField('Engine Hours', 'engine_hours', 'number')}
        </div>
      </div>

      {/* Electrical Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Electrical</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Voltage', 'voltage', 'select', voltages)}
          {renderField('Shorepower', 'shorepower', 'select', shorepowerOptions)}
          {renderField('Shorepower Qty', 'shorepower_qty', 'select', shorepowerQtyOptions)}
          {renderField('Battery Capacity', 'battery_capacity', 'text')}
          {renderField('Alternator Output', 'alternator_output', 'text')}
        </div>
      </div>

      {/* Tank Capacities Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tank Capacities</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Fresh Water Capacity', 'fresh_water_capacity', 'number')}
          {renderField('Waste Water Capacity', 'waste_water_capacity', 'number')}
          {renderField('Holding Tank Capacity', 'holding_tank_capacity', 'number')}
        </div>
      </div>

      {/* Performance Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Max Speed', 'max_speed', 'text')}
          {renderField('Cruising Speed', 'cruising_speed', 'text')}
          {renderField('Range', 'range', 'text')}
        </div>
      </div>

      {/* Accommodations Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Accommodations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Berths', 'berths', 'number')}
          {renderField('Cabins', 'cabins', 'number')}
          {renderField('Heads', 'heads', 'number')}
          {renderField('Galley', 'galley', 'text')}
        </div>
      </div>

      {/* Safety Equipment Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Safety Equipment</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Life Rafts', 'life_rafts', 'text')}
          {renderField('Life Jackets', 'life_jackets', 'text')}
          {renderField('Fire Extinguishers', 'fire_extinguishers', 'text')}
          {renderField('Flares', 'flares', 'text')}
        </div>
      </div>

      {/* Insurance & Documentation Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Insurance & Documentation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Insurance Company', 'insurance_company', 'text')}
          {renderField('Insurance Policy', 'insurance_policy', 'text')}
          {renderField('Insurance Expiry', 'insurance_expiry', 'date')}
          {renderField('Survey Date', 'survey_date', 'date')}
          {renderField('Survey Expiry', 'survey_expiry', 'date')}
        </div>
      </div>

      {/* Additional Info Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {renderField('Purchase Date', 'purchase_date', 'date')}
          {renderField('Purchase Price', 'purchase_price', 'number')}
          {renderField('Current Value', 'current_value', 'number')}
        </div>
        <div className="mt-6">
          {renderField('Notes', 'notes', 'textarea')}
        </div>
      </div>
    </div>
  );
};

export default VesselDetailsTab;
